/**
 * RECORDS FROM THE CLIENT'S CONNECTED BOOKS (ADR-0024 T5.4; P4).
 *
 * Offered beside the upload, on a work that still needs them, when the client's books are connected and the client
 * consented to Leveri reading what they need: a bank reconciliation's cash book (the period's ledger and the trial
 * balance as at the day before it), or a control account's balances (the trial balance and the supplier or customer
 * balances at the cut-off — one offer per subledger the work could be reconciled against). Reading is the server's act, or a plain sentence saying why not; the screen offers it and shows what
 * came back. The system's name is the connection's own; the browser holds no list of systems.
 *
 * Secondary, never the primary: "Start the work" stays the one saturated element on this screen.
 */
import { useEffect, useState } from "react";
import { ConnectionsResponse, WorkActionResponse, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

/** What each reading needs the client to have consented to, and how it is offered. */
const READINGS = {
  CASH_BOOK: {
    kinds: ["ledger.transactions", "ledger.trial_balance"],
    action: "Read the cash book from",
    says: "The client's ledger for this period and its opening balance, read from their books.",
    testId: "connected-cash-book",
  },
  SUPPLIERS: {
    kinds: ["ledger.trial_balance", "ledger.party_balances"],
    action: "Read the trial balance and supplier balances from",
    says: "What each supplier is owed at the cut-off, and the trial balance, read from the client's books.",
    testId: "connected-balances-suppliers",
  },
  CUSTOMERS: {
    kinds: ["ledger.trial_balance", "ledger.party_balances"],
    action: "Read the trial balance and customer balances from",
    says: "What each customer owes at the cut-off, and the trial balance, read from the client's books.",
    testId: "connected-balances-customers",
  },
} as const;

export interface ConnectedRecordsProps {
  reading: keyof typeof READINGS;
  workId: string;
  clientId: string;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function ConnectedRecords({ reading, workId, clientId, onWork, onOutcome }: ConnectedRecordsProps) {
  const offer = READINGS[reading];
  const [system, setSystem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    call("GET", `/api/clients/${clientId}/connections`, ConnectionsResponse)
      .then((r) => {
        const usable = r.connections.find((c) => c.status === "ACTIVE" && offer.kinds.every((kind) => c.evidenceKinds.includes(kind)));
        if (live) setSystem(usable ? usable.systemName : null);
      })
      // Not knowing is not offering: the upload is always there.
      .catch(() => { if (live) setSystem(null); });
    return () => { live = false; };
  }, [clientId, offer]);

  if (!system) return null;

  async function read() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      // A control account's subledger is said; the server reads exactly that one (a cash book needs no choice).
      const result = await call("POST", `/api/work/${workId}/connected-records`, WorkActionResponse, reading === "CASH_BOOK" ? {} : { subledger: reading });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-5" data-testid={offer.testId}>
      <div className="flex flex-wrap items-center gap-3.5">
        <Button variant="secondary" disabled={busy} onClick={() => void read()}>{offer.action} {system}</Button>
        <span className="text-[13px] text-stone">{offer.says}</span>
      </div>
      {busy && <div className="mt-2"><Reading>Reading the books…</Reading></div>}
      {failure && <div className="mt-2"><Notice>{failure}</Notice></div>}
    </div>
  );
}
